import { uniqueSorted } from '@/utils/array';
import { isFiniteNumber } from '@/utils/number';
import { stableStringify } from '@/utils/object';
import type { MarketMakerInput, ProgramInput, Session } from '../mm-program-schema';

/*
 * The liquidity the programs ask for on a market. A bucket asks for `kpi` USD, both sides together, quoted within `bps`
 * of mid. B2 is the wider bucket and its size includes what is quoted inside B1, so one market maker's requirement
 * within a width is the largest size among its buckets at most that wide, not their sum. The market's requirement is
 * the sum over market makers. An NBBO bucket is wider than its nominal bps by half the external spread, so what it
 * asks for is kept apart rather than placed exactly on the axis.
 */

export interface DepthSource {
  pid: string;
  marketMaker: MarketMakerInput;
}

export interface DepthBucket {
  bucket: 'b1' | 'b2';
  bps: number;
  kpi: number;
  nbbo: boolean;
}

export interface MarketMakerDepth {
  pid: string;
  name: string;
  active: boolean;
  program: ProgramInput;
  buckets: DepthBucket[];
  /** `PROVIDER symbol` widening its NBBO buckets */
  nbbSource: string | null;
}

export interface DepthLevel {
  bps: number;
  total: number;
  /** The part of `total` asked for by NBBO buckets, which reach further than `bps` */
  nbboTotal: number;
  /** The bucket that sets each market maker's requirement at this width; absent while none is this narrow */
  byPid: Map<string, DepthBucket>;
}

export interface InstrumentDepth {
  instrument: string;
  day: string;
  session: Session;
  /** Whether any schedule in force sets its buckets per session */
  sessioned: boolean;
  marketMakers: MarketMakerDepth[];
  /** One per distinct bucket width, narrowest first */
  levels: DepthLevel[];
}

const dayOf = (value: string) => value.slice(0, 10);

const isInForce = (program: ProgramInput, day: string) => {
  const start = dayOf(program.start_date);
  const end = program.end_date.trim() ? dayOf(program.end_date) : null;
  return Boolean(start) && start <= day && (end === null || day < end);
};

/** The schedule that sets a session's buckets on a day: the session's own one, or one for all hours. */
export const programInForce = (programs: ProgramInput[], day: string, session: Session) =>
  programs.find((program) => isInForce(program, day) && (program.session === undefined || program.session === session));

const bucketsOf = (program: ProgramInput): DepthBucket[] =>
  (['b1', 'b2'] as const).flatMap((bucket) => {
    const { bps, kpi, nbbo } = program[bucket];
    return isFiniteNumber(bps) && isFiniteNumber(kpi) && kpi > 0 ? [{ bucket, bps, kpi, nbbo: nbbo === true }] : [];
  });

/** The bucket setting a market maker's requirement within `bps`: the largest size among buckets at most that wide. */
export const requirementWithin = (buckets: DepthBucket[], bps: number): DepthBucket | undefined =>
  buckets.filter((bucket) => bucket.bps <= bps).reduce<DepthBucket | undefined>((best, bucket) => (!best || bucket.kpi > best.kpi ? bucket : best), undefined);

export const instrumentDepth = (sources: DepthSource[], instrument: string, day: string, session: Session, includeInactive = false): InstrumentDepth => {
  let sessioned = false;
  const marketMakers = sources.flatMap(({ pid, marketMaker }): MarketMakerDepth[] => {
    if (!includeInactive && marketMaker.active !== true) return [];
    const programs = marketMaker.programs?.[instrument] ?? [];
    if (programs.some((program) => program.session !== undefined && isInForce(program, day))) sessioned = true;
    const program = programInForce(programs, day, session);
    if (!program) return [];
    const buckets = bucketsOf(program);
    if (!buckets.length) return [];
    return [
      {
        pid,
        name: marketMaker.name,
        active: marketMaker.active === true,
        program,
        buckets,
        nbbSource: program.nbb_provider && program.nbb_instrument ? `${program.nbb_provider} ${program.nbb_instrument}` : null,
      },
    ];
  });
  const widths = uniqueSorted(
    marketMakers.flatMap((marketMaker) => marketMaker.buckets.map((bucket) => bucket.bps)),
    (left, right) => left - right,
  );
  const levels = widths.map((bps): DepthLevel => {
    const byPid = new Map<string, DepthBucket>();
    let total = 0;
    let nbboTotal = 0;
    for (const marketMaker of marketMakers) {
      const bucket = requirementWithin(marketMaker.buckets, bps);
      if (!bucket) continue;
      byPid.set(marketMaker.pid, bucket);
      total += bucket.kpi;
      if (bucket.nbbo) nbboTotal += bucket.kpi;
    }
    return { bps, total, nbboTotal, byPid };
  });
  return { instrument, day, session, sessioned, marketMakers, levels };
};

/** What the market asks for within `bps` of mid. */
export const depthWithin = (depth: Pick<InstrumentDepth, 'levels'>, bps: number) => {
  const level = depth.levels.filter((item) => item.bps <= bps).at(-1);
  return { total: level?.total ?? 0, nbboTotal: level?.nbboTotal ?? 0 };
};

/** Days after `day` on which a schedule on the market starts or ends, so its requirement may change. */
export const changeDaysAfter = (sources: DepthSource[], instrument: string, day: string, includeInactive = false) =>
  uniqueSorted(
    sources.flatMap(({ marketMaker }) =>
      includeInactive || marketMaker.active === true
        ? (marketMaker.programs?.[instrument] ?? []).flatMap((program) => [dayOf(program.start_date), dayOf(program.end_date)]).filter((change) => change > day)
        : [],
    ),
  );

export interface MarketSummary {
  instrument: string;
  /** The market as each session sees it; identical for a market without per-session schedules */
  bySession: Record<Session, InstrumentDepth>;
  /** Whether any schedule in force sets its buckets per session */
  sessioned: boolean;
  /** Whether Core, Extended and Weekend ask for different liquidity */
  sessionsDiffer: boolean;
  nextChange: string | null;
}

const SESSION_KEYS = ['C', 'E', 'W'] as const satisfies readonly Session[];

const levelsKey = (depth: InstrumentDepth) => stableStringify(depth.levels.map((level) => [level.bps, level.total, level.nbboTotal]));

/** Every market with a schedule in force on `day` or starting after it, read for each session. */
export const summariseMarkets = (sources: DepthSource[], day: string, includeInactive = false): MarketSummary[] => {
  const instruments = uniqueSorted(sources.flatMap(({ marketMaker }) => Object.keys(marketMaker.programs ?? {})));
  return instruments.flatMap((instrument) => {
    const bySession = Object.fromEntries(SESSION_KEYS.map((session) => [session, instrumentDepth(sources, instrument, day, session, includeInactive)])) as Record<
      Session,
      InstrumentDepth
    >;
    const nextChange = changeDaysAfter(sources, instrument, day, includeInactive)[0] ?? null;
    if (!bySession.C.marketMakers.length && !bySession.E.marketMakers.length && !bySession.W.marketMakers.length && !nextChange) return [];
    const sessioned = bySession.C.sessioned;
    const sessionsDiffer = sessioned && new Set(SESSION_KEYS.map((session) => levelsKey(bySession[session]))).size > 1;
    return [{ instrument, bySession, sessioned, sessionsDiffer, nextChange }];
  });
};

export interface LadderRow {
  bps: number;
  /** What each session asks for within `bps`; a market without per-session schedules has the same in all three */
  bySession: Record<Session, { total: number; nbboTotal: number }>;
}

/** One row per distinct bucket width on the market in any session, so each session's exact thresholds line up. */
export const sessionLadder = (bySession: Record<Session, InstrumentDepth>, sessions: readonly Session[] = SESSION_KEYS): LadderRow[] =>
  uniqueSorted(
    sessions.flatMap((session) => bySession[session].levels.map((level) => level.bps)),
    (left, right) => left - right,
  ).map((bps) => ({
    bps,
    bySession: Object.fromEntries(SESSION_KEYS.map((session) => [session, depthWithin(bySession[session], bps)])) as LadderRow['bySession'],
  }));
